/**
 * Embedding-store schema (I.E.5 / #42).
 *
 * Phase 1 is schema-only — see `./vector-lane.ts` for the lane stub that
 * registers with the router and returns `[]`. Live embedding generation
 * arrives in Phase 2 with the AI Gateway path.
 *
 * Schema parity:
 *   - apps/do-workspace/migrations/0004_embeddings.sql
 *   - packages/replica/src/schema.ts (version 7)
 *
 * The DDL is exported as constants so the migrations files and any test
 * that wants to assert "the embedding tables exist in both stores" pull
 * from a single source.
 */

/**
 * The model registry — one row per (named, deprecated) embedding model.
 * `dim` is the vector dimensionality the model produces (used for sanity
 * checks at insert time). `deprecated_at` is set when the model is rolled
 * forward; the registry retains the row so historical vectors stay
 * interpretable.
 */
export const EMBEDDING_MODELS_TABLE = 'embedding_models';
export const EMBEDDING_MODELS_DDL = `
  CREATE TABLE IF NOT EXISTS ${EMBEDDING_MODELS_TABLE} (
    id              TEXT PRIMARY KEY,
    name            TEXT NOT NULL,
    dim             INTEGER NOT NULL CHECK (dim > 0),
    deprecated_at   TEXT
  );
`;

/**
 * One vector per (fact, model). `vector` is a packed Float32 blob —
 * the consumer code unpacks via `new Float32Array(buf)`. Phase 2 may
 * introduce sqlite-vss / sqlite-vec; the lookup path will then swap
 * the cosine-similarity scan for the vector index. The migration is
 * a no-op until then.
 */
export const EMBEDDINGS_TABLE = 'embeddings';
export const EMBEDDINGS_DDL = `
  CREATE TABLE IF NOT EXISTS ${EMBEDDINGS_TABLE} (
    id          TEXT PRIMARY KEY,
    fact_id     TEXT NOT NULL,
    model_id    TEXT NOT NULL,
    vector      BLOB NOT NULL,
    created_at  TEXT NOT NULL,
    FOREIGN KEY (model_id) REFERENCES ${EMBEDDING_MODELS_TABLE}(id)
  );
  CREATE INDEX IF NOT EXISTS embeddings_fact_id
    ON ${EMBEDDINGS_TABLE} (fact_id);
  CREATE INDEX IF NOT EXISTS embeddings_model_id
    ON ${EMBEDDINGS_TABLE} (model_id);
`;

/** Combined DDL block used by the migration files (server + client). */
export const EMBEDDING_STORE_DDL = `${EMBEDDING_MODELS_DDL}\n${EMBEDDINGS_DDL}`;
